import { useState } from "react";
import { Aviso, Botao, Cartao, Chip, GrupoDeOpcoes, Opcao } from "./ui";
import type { EstadoDoChip } from "./ui";
import { ErroDaApi } from "../nucleo/api";
import { INTERVALOS_DE_ATUALIZACAO, useSaude } from "../nucleo/saude";
import type { IntervaloDeAtualizacao } from "../nucleo/saude";
import { CHIP_DA_CUSTODIA, ROTULO_DA_CUSTODIA } from "../nucleo/cofre";

const ROTULO_DO_INTERVALO: Record<IntervaloDeAtualizacao, string> = {
  0: "Só quando eu pedir",
  15_000: "A cada 15 segundos",
  60_000: "A cada minuto",
};

const horario = new Intl.DateTimeFormat("pt-BR", { timeStyle: "medium" });

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroDaApi) return erro.message;
  if (erro instanceof Error) return erro.message;
  return "Falha desconhecida ao consultar a API.";
}

/**
 * O gate da F0: prova, na tela, que o navegador alcança a API e que a API alcança o banco.
 * Enquanto a resposta não chega o estado é "consultando" — nunca "ok" por otimismo.
 */
export function PainelDeSaude() {
  const [intervalo, setIntervalo] = useState<IntervaloDeAtualizacao>(0);
  const saude = useSaude(intervalo);

  const estadoDaApi: EstadoDoChip = saude.isSuccess
    ? "ok"
    : saude.isError
      ? "erro"
      : "neutro";

  const rotuloDaApi = saude.isSuccess
    ? "no ar"
    : saude.isError
      ? "sem resposta"
      : "consultando";

  const estadoDoBanco: EstadoDoChip = saude.isSuccess
    ? saude.data.banco === "ok"
      ? "ok"
      : "erro"
    : "neutro";

  const rotuloDoBanco = saude.isSuccess
    ? saude.data.banco === "ok"
      ? "conectado"
      : "sem conexão"
      : saude.isError
        ? "desconhecido"
        : "consultando";

  /* O cofre entra no painel porque sem ele a tela mentia: `provisoria` e `indisponivel` derrubam
     o `status` do servidor para `degradado`, e este cartão continuava mostrando "API no ar,
     banco conectado" sem nenhuma pista do porquê. Estado do ambiente que esconde a metade
     degradada não é estado do ambiente. */
  const estadoDoCofre: EstadoDoChip = saude.isSuccess
    ? CHIP_DA_CUSTODIA[saude.data.cofre]
    : "neutro";

  const rotuloDoCofre = saude.isSuccess
    ? ROTULO_DA_CUSTODIA[saude.data.cofre]
    : saude.isError
      ? "desconhecida"
      : "consultando";

  return (
    <Cartao
      className="mb-6"
      titulo="Estado do ambiente"
      acessorio={
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          onClick={() => void saude.refetch()}
          disabled={saude.isFetching}
        >
          {saude.isFetching ? "Verificando…" : "Verificar agora"}
        </Botao>
      }
    >
      {/* A explicação do proxy do Vite só faz sentido para quem roda o projeto na
          própria máquina. Em produção a mesma frase confunde: lá quem encaminha
          `/api/` é o nginx do host, e ninguém tem um uvicorn em 127.0.0.1:8000. */}
      <p className="cartao__texto mb-4">
        Consulta <code>GET /api/saude</code>.{" "}
        {import.meta.env.DEV
          ? "Em desenvolvimento o Vite encaminha para o uvicorn em 127.0.0.1:8000."
          : "O nginx do host encaminha /api/ para a API, na mesma origem do portal."}
      </p>

      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 m-0">
        <div>
          <dt className="campo__rotulo">API</dt>
          <dd className="m-0">
            <Chip estado={estadoDaApi} data-testid="estado-api">
              {rotuloDaApi}
            </Chip>
          </dd>
        </div>

        <div>
          <dt className="campo__rotulo">Banco</dt>
          <dd className="m-0">
            <Chip estado={estadoDoBanco} data-testid="estado-banco">
              {rotuloDoBanco}
            </Chip>
          </dd>
        </div>

        <div>
          <dt className="campo__rotulo">Cofre de credenciais</dt>
          <dd className="m-0">
            <Chip estado={estadoDoCofre} data-testid="estado-do-cofre">
              {rotuloDoCofre}
            </Chip>
          </dd>
        </div>

        <div>
          <dt className="campo__rotulo">Versão</dt>
          <dd className="m-0 numero-tabular text-sm" data-testid="versao-api">
            {saude.data?.versao ?? "—"}
          </dd>
        </div>

        <div>
          <dt className="campo__rotulo">Ambiente</dt>
          <dd className="m-0 text-sm" data-testid="ambiente-api">
            {saude.data?.ambiente ?? "—"}
          </dd>
        </div>
      </dl>

      {saude.isSuccess && saude.data.cofre === "provisoria" ? (
        <Aviso tom="atencao" className="mt-4" data-testid="cofre-provisorio">
          <b>A chave mestra do cofre está num arquivo deste servidor.</b> O desenho previsto
          guarda essa chave num serviço gerenciado, fora do host — enquanto isso não acontece, as
          credenciais guardadas têm a proteção de um arquivo do servidor, e não a de um cofre
          externo. Está dito aqui porque um cofre que parece seguro e não é é pior do que um campo
          de texto: quem usa passa a confiar.
        </Aviso>
      ) : null}

      {saude.isSuccess && saude.data.cofre === "indisponivel" ? (
        <Aviso tom="erro" className="mt-4" data-testid="cofre-indisponivel">
          <b>O cofre está parado.</b> A custódia escolhida para a chave mestra não responde, e o
          portal recusa guardar e revelar credencial em vez de cair para uma chave mais fraca.
          Quem opera a API precisa corrigir a configuração; até lá, os campos com cadeado da
          trilha de infraestrutura não aceitam senha.
        </Aviso>
      ) : null}

      {saude.isError ? (
        <Aviso tom="erro" className="mt-4">
          <b>A API não respondeu.</b> {mensagemDoErro(saude.error)} Suba o backend com{" "}
          <code>uv run uvicorn app.main:app --port 8000</code> dentro de <code>api/</code>.
        </Aviso>
      ) : null}

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <GrupoDeOpcoes rotulo="Atualização automática" className="max-w-[320px] grow">
          {INTERVALOS_DE_ATUALIZACAO.map((valor) => (
            <Opcao
              key={valor}
              tipo="radio"
              name="intervalo-de-atualizacao"
              value={String(valor)}
              checked={intervalo === valor}
              onChange={() => setIntervalo(valor)}
              titulo={ROTULO_DO_INTERVALO[valor]}
            />
          ))}
        </GrupoDeOpcoes>

        <p className="nota-rodape m-0">
          Última verificação:{" "}
          <span className="numero-tabular">
            {saude.dataUpdatedAt > 0 ? horario.format(saude.dataUpdatedAt) : "ainda não"}
          </span>
        </p>
      </div>
    </Cartao>
  );
}
